import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const ContentHeader = ({ toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { 
    themeColor, 
    savedDbColor, 
    previewTheme, 
    applyThemeToDB, 
    cancelChanges, 
    resetTheme 
  } = useTheme();

  const hasUnsavedChanges = themeColor !== savedDbColor;

  // ✅ Export Dropdown States & Refs
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApply = async () => {
    await applyThemeToDB();
  };

  const handleCancel = () => {
    cancelChanges(); 
  };

  // ✅ Dispatch specific export events (excel, pdf, print)
  const handleExportOption = (type) => {
    setShowExportMenu(false);
    const event = new CustomEvent("exportData", { detail: { type } });
    window.dispatchEvent(event);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    const titles = {
      "/dashboard": "Dashboard",
      "/user/create": "User Creation",
      "/user/Role/Creation": "Roles Creation",
      "/Room/Form": "Room Form",
      "/DepartmentForm": "Department Form",
      "/EmployeeForm": "Employee Form",
      "/DiscountAuthorityForm": "Discount Authority",
      "/PharmaCompanyForm": "Pharma Company",
      "/opd/register": "Patient Registration",
      "/opd/list": "OPD List",
      "/IpdList": "IPD List",
      "/IpdAdvance": "Advance Payment",
      "/IpdServiceDetails": "Service Details",
      "/Room/Booking/Form": "Room Booking",
      "/DischargeBill": "Discharge Bill",
      "/DischargeList": "Discharge List",
      "/BillingForm": "Billing Form",
      "/BillList": "Billing List",
      "/BillingPayment": "Billing Payment",
      "/Report": "Reports",
      "/AccountMasterForm": "Account Master",
      "/BankEntryForm": "Bank Entry",
      "/ExpenseForm": "Expense Entry",
      "/ExPDTReport": "Expense Report",
      "/DaybookReport": "DayBook Report",
      "/MedCategoryForm": "Medicine Category",
      "/MedBrandForm": "Medicine Brand",
      "/MedicineForm": "Medicine Form",
      "/VendorForm": "Vendor Entry",
      "/MedPurchase": "Medicine Purchase",
      "/MedicinePurchaseSearch": "Purchase Search",
      "/MedicineSaleForm": "Medicine Sale",
      "/MedicineSaleSearch": "Sale Search",
      "/StockOutForm": "Stock Out",
      "/StockOutSearch": "Stock Out Search",
      "/DebitVoucher": "Debit Voucher",
      "/DebitVoucherSearch": "Debit Voucher Search",
      "/SalesReports": "Sales Reports",
    };
    return titles[path] || "Hospital Management System";
  };

  const getBreadcrumb = () => {
    const pathArray = location.pathname.split("/").filter((p) => p);
    const nameMapping = {
      "user": "User", "create": "Create", "Role": "Role", "Creation": "Creation",
      "Room": "Room", "Form": "Form", "opd": "OPD", "register": "Register",
      "list": "List", "dashboard": "Dashboard", "Billing": "Billing",
      "Medicine": "Medicine", "Purchase": "Purchase", "Sale": "Sale",
      "Search": "Search", "Debit": "Debit", "Voucher": "Voucher",
      "Stock": "Stock", "Out": "Out", "Reports": "Reports"
    };

    return pathArray.map((segment, index) => {
      const displayName = nameMapping[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      const path = "/" + pathArray.slice(0, index + 1).join("/");
      return { name: displayName, path, isLast: index === pathArray.length - 1 };
    });
  };

  const breadcrumbs = getBreadcrumb();

  const getPageIcon = () => {
    const path = location.pathname;
    if (path.includes("dashboard")) return "bi-speedometer2";
    if (path.includes("patient") || path.includes("register")) return "bi-person-plus-fill";
    if (path.includes("Report")) return "bi-graph-up";
    return "bi-file-earmark-text";
  };

  const handleRefresh = () => window.location.reload();
  const handlePrint = () => window.print();
  const handleGoBack = () => navigate(-1);

  return (
    <div className="content-header-modern">
      <div className="header-container">
        
        {/* Left Section */}
        <div className="header-left">
          <button className="sidebar-toggle-btn" onClick={toggleSidebar} title="Toggle Sidebar">
            <i className="bi bi-layout-sidebar-inset"></i>
          </button>

          <div className="page-info">
            <div className="page-title-row">
              <div className="page-icon">
                <i className={`bi ${getPageIcon()}`}></i>
              </div>
              <h1 className="page-title">{getPageTitle()}</h1>
            </div>
            
            <nav className="breadcrumb-nav">
              <i className="bi bi-house-door breadcrumb-icon"></i>
              <span className="breadcrumb-item" onClick={() => navigate("/dashboard")} style={{ cursor: "pointer" }}>
                Home
              </span>
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={idx}>
                  <i className="bi bi-chevron-right breadcrumb-separator"></i>
                  <span
                    className={`breadcrumb-item ${crumb.isLast ? "active" : ""}`}
                    onClick={() => !crumb.isLast && navigate(crumb.path)}
                    style={{ cursor: crumb.isLast ? "default" : "pointer" }}
                  >
                    {crumb.name}
                  </span>
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        {/* Right Section */}
        <div className="header-right">

          {/* THEME CONTROLS */}
          <div className="theme-controls d-flex align-items-center gap-2 me-2 p-1 rounded border bg-light">
            <label className="theme-label" style={{fontSize: '12px', fontWeight: 'bold', color:'#555', marginBottom: 0, paddingLeft:'5px'}}>Theme:</label>
            
            <input 
              type="color" 
              value={themeColor}
              onChange={(e) => previewTheme(e.target.value)} 
              title="Pick a color"
              style={{
                width: '30px', 
                height: '30px', 
                border: 'none', 
                cursor: 'pointer',
                background: 'transparent',
                padding: 0
              }}
            />

            {hasUnsavedChanges ? (
              <div className="d-flex gap-1 animate-fade-in">
                <button 
                  onClick={handleApply}
                  className="btn btn-sm btn-success p-1 px-2 d-flex align-items-center"
                  title="Apply Changes"
                >
                  <i className="bi bi-check-lg"></i>
                </button>

                <button 
                  onClick={handleCancel}
                  className="btn btn-sm btn-danger p-1 px-2 d-flex align-items-center"
                  title="Cancel"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            ) : (
              <button 
                onClick={resetTheme}
                className="btn btn-sm btn-outline-secondary p-1 px-2 d-flex align-items-center"
                title="Reset"
                style={{ border: 'none' }}
              >
                <i className="bi bi-arrow-counterclockwise"></i>
              </button>
            )}
          </div>

          <button className="header-action-btn" onClick={handleGoBack} title="Go Back">
            <i className="bi bi-arrow-left"></i>
            <span className="btn-text">Back</span>
          </button>

          <button className="header-action-btn" onClick={handleRefresh} title="Refresh Page">
            <i className="bi bi-arrow-clockwise"></i>
            <span className="btn-text">Refresh</span>
          </button>

          {/* ✅ UPDATED: Export Dropdown Menu */}
          <div className="position-relative desktop-only" ref={exportMenuRef}>
            <button 
              className="header-action-btn export-btn" 
              onClick={() => setShowExportMenu(!showExportMenu)} 
              title="Export Options"
            >
              <i className="bi bi-download"></i>
              <span className="btn-text ms-1">
                Export <i className="bi bi-chevron-down ms-1" style={{fontSize:'10px', strokeWidth: '2px'}}></i>
              </span>
            </button>

            {/* Dropdown Options */}
            {showExportMenu && (
              <div 
                className="export-dropdown-menu shadow rounded bg-white position-absolute end-0 mt-2 p-2 border" 
                style={{ zIndex: 1050, minWidth: '140px', animation: 'fadeIn 0.2s ease-in-out' }}
              >
                <button className="export-option-btn w-100 text-start" onClick={() => handleExportOption('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2 fs-6"></i> Excel
                </button>
                <button className="export-option-btn w-100 text-start" onClick={() => handleExportOption('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2 fs-6"></i> PDF
                </button>
                <button className="export-option-btn w-100 text-start" onClick={() => handleExportOption('print')}>
                  <i className="bi bi-printer text-primary me-2 fs-6"></i> Print
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      <style jsx>{`
        .content-header-modern {
          background: white;
          border-bottom: 2px solid #e0e6ed;
          box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
          position: sticky;
          top: 0;
          z-index: 100;
          padding: clamp(4px, 0.4vw, 6px) clamp(8px, 0.8vw, 12px);
          margin-bottom: clamp(6px, 0.6vw, 8px);
          width: 100%;
          box-sizing: border-box;
        }

        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: clamp(6px, 0.6vw, 8px);
          max-width: 100%;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
        }

        .sidebar-toggle-btn {
          width: clamp(24px, 2vw, 28px);
          height: clamp(24px, 2vw, 28px);
          border-radius: clamp(5px, 0.5vw, 6px);
          border: none;
          background: linear-gradient(135deg, #f0f4f8 0%, #e0eaf3 100%);
          color: var(--theme-color);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .sidebar-toggle-btn:hover {
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          color: white;
          transform: scale(1.05);
        }

        .sidebar-toggle-btn i { font-size: clamp(11px, 1vw, 13px); }

        .page-info { 
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          flex: 1;
        }

        .page-title-row { 
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-icon {
          width: clamp(24px, 2vw, 28px);
          height: clamp(24px, 2vw, 28px);
          border-radius: clamp(5px, 0.5vw, 6px);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          flex-shrink: 0;
        }

        .page-icon i { font-size: clamp(12px, 1.1vw, 14px); }

        .page-title {
          margin: 0;
          font-size: clamp(12px, 1.1vw, 14px);
          font-weight: 700;
          color: #2c3e50;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .breadcrumb-nav {
          display: flex;
          align-items: center;
          gap: clamp(3px, 0.3vw, 4px);
          font-size: clamp(8px, 0.65vw, 9px);
          color: #7f8c8d;
          padding-left: clamp(28px, 2.5vw, 32px);
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .breadcrumb-item:not(.active):hover { color: var(--theme-color); }
        .breadcrumb-item.active { color: var(--theme-color); font-weight: 600; }

        .header-right {
          display: flex;
          align-items: center;
          gap: clamp(4px, 0.5vw, 6px);
          flex-shrink: 0;
        }

        .header-action-btn {
          display: flex;
          align-items: center;
          gap: clamp(3px, 0.3vw, 4px);
          padding: clamp(4px, 0.4vw, 6px) clamp(8px, 0.8vw, 10px);
          border-radius: clamp(4px, 0.4vw, 5px);
          border: 1px solid #e0e6ed;
          background: white;
          color: var(--theme-color);
          cursor: pointer;
          font-size: clamp(9px, 0.75vw, 10px);
          font-weight: 600;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .header-action-btn:hover {
          background: var(--theme-color);
          color: white;
          border-color: var(--theme-color);
        }

        .header-action-btn i { font-size: clamp(10px, 0.9vw, 12px); }

        .export-btn {
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          color: white;
          border: none;
        }

        /* ✅ Custom Export Dropdown Button Style */
        .export-option-btn {
          border: none;
          background: transparent;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          color: #334155;
          transition: background 0.2s ease;
        }
        .export-option-btn:hover {
          background: #f1f5f9;
          color: var(--theme-color);
        }

        .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 992px) {
          .btn-text { display: none; }
          .header-action-btn { 
            padding: 10px;
            min-width: 40px;
            justify-content: center;
          }
          .page-title { font-size: 16px; }
          .desktop-only { display: none !important; }
        }

        @media (max-width: 768px) {
          .content-header-modern {
            padding: 5px 12px;
            margin-bottom: 10px;
            position: relative; 
          }

          .header-container {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .header-left {
            width: 100%;
            gap: 10px; 
          }

          .page-icon { 
            width: 28px;
            height: 28px;
          }
          .page-icon i { font-size: 14px; }
          
          .page-title { 
            font-size: 13px;
            max-width: calc(100vw - 140px);
          }
          
          .breadcrumb-nav { display: none; }

          .header-right {
            width: 100%;
            justify-content: flex-end;
            gap: 6px;
          }

          .theme-label { display: none; }
          
          .theme-controls {
            margin-right: auto;
            padding: 2px 5px !important;
            background: transparent !important;
            border: none !important;
          }

          .sidebar-toggle-btn {
            width: 28px;
            height: 28px;
          }
          .sidebar-toggle-btn i {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContentHeader;